import { csvDateStamp } from '../../utils/csvExport';
import { SESSION_EXPORT_COLUMNS, ALL_SESSIONS_COLUMNS, flattenSessionRows, scanStatus, slug } from './sessionExportData';

// Export Excel des participants par séance : mêmes colonnes que le CSV, avec l'en-tête aux couleurs
// Wilout et le statut de scan en couleur (mêmes teintes que la page publique : vert / rouge / ambre).
// `xlsx-js-style` (fork de xlsx qui écrit les styles de cellule) est chargé à la demande : il ne pèse
// pas sur le chargement de la page.

const HEADER_STYLE = {
  fill: { patternType: 'solid', fgColor: { rgb: '002F36' } },
  font: { bold: true, color: { rgb: 'FFFFFF' } },
  alignment: { vertical: 'center' },
};
const SCAN_STYLES = {
  yes: { fill: { patternType: 'solid', fgColor: { rgb: 'E6F8F4' } }, font: { bold: true, color: { rgb: '006B59' } }, alignment: { horizontal: 'center' } },
  no: { fill: { patternType: 'solid', fgColor: { rgb: 'FFEDEA' } }, font: { bold: true, color: { rgb: '970000' } }, alignment: { horizontal: 'center' } },
  partial: { fill: { patternType: 'solid', fgColor: { rgb: 'FFF9DE' } }, font: { bold: true, color: { rgb: '8A5A00' } }, alignment: { horizontal: 'center' } },
};
const MIN_WIDTH = 10;
const MAX_WIDTH = 42;

const cellValue = (column, row) => {
  const value = column.value ? column.value(row) : row[column.key];
  return value === null || value === undefined ? '' : value;
};

/** Onglet : le nom ne peut pas dépasser 31 caractères ni contenir [ ] : * ? / \. */
const sheetName = (text) => String(text || '').replace(/[[\]:*?/\\]/g, ' ').trim().slice(0, 31) || 'Participants';

/** Construit le classeur (pur, testé) ; `XLSX` est la bibliothèque chargée. */
export const buildWorkbook = (XLSX, columns, rows, name) => {
  const header = columns.map((c) => ({ v: c.label, t: 's', s: HEADER_STYLE }));
  const body = rows.map((row) => columns.map((c) => {
    const value = cellValue(c, row);
    const cell = { v: value, t: typeof value === 'number' ? 'n' : 's' };
    if (c.key === 'scanne') cell.s = SCAN_STYLES[scanStatus(row).tone];
    return cell;
  }));
  const sheet = XLSX.utils.aoa_to_sheet([header, ...body]);
  sheet['!cols'] = columns.map((c, i) => ({
    wch: Math.min(Math.max(MIN_WIDTH, c.label.length + 2, ...body.map((r) => String(r[i].v).length + 2)), MAX_WIDTH),
  }));
  sheet['!autofilter'] = { ref: XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: Math.max(rows.length, 1), c: columns.length - 1 } }) };
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, sheet, sheetName(name));
  return workbook;
};

const loadXlsx = async () => {
  const mod = await import('xlsx-js-style');
  return mod.default || mod;
};

/** Télécharge l'Excel d'une séance (résultat de l'API : { eventName, session, rows }). */
export const downloadSessionXlsx = async ({ eventName, session, rows }) => {
  const XLSX = await loadXlsx();
  const workbook = buildWorkbook(XLSX, SESSION_EXPORT_COLUMNS, rows, session.nom);
  XLSX.writeFile(workbook, `participants_${slug(eventName || 'event')}_${slug(session.nom)}_${csvDateStamp()}.xlsx`);
};

/**
 * Télécharge un seul Excel pour plusieurs séances (résultat de l'API : { eventName, sessions: [{ session, rows }] }).
 * Retourne le nombre de lignes, 0 (sans rien télécharger) s'il n'y en a pas.
 */
export const downloadAllSessionsXlsx = async ({ eventName, sessions }, label = 'toutes_les_seances') => {
  const rows = flattenSessionRows(sessions);
  if (rows.length === 0) return 0;
  const XLSX = await loadXlsx();
  const workbook = buildWorkbook(XLSX, ALL_SESSIONS_COLUMNS, rows, 'Participants');
  XLSX.writeFile(workbook, `participants_${slug(eventName || 'event')}_${slug(label)}_${csvDateStamp()}.xlsx`);
  return rows.length;
};
